import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface TagInstanceNavigationTag {
  tagId: unknown;
  normalizedValue: string;
}

export interface TagInstanceNavigationDetection {
  _id: unknown;
  normalizedText: string;
  pageNumber: number;
  documentOrder?: number;
  bbox?: { x: number; y: number; width: number; height: number };
}

export interface UseTagInstanceNavigationArgs<
  TTag extends TagInstanceNavigationTag,
  TDetection extends TagInstanceNavigationDetection,
> {
  tags: TTag[];
  activeDetections: TDetection[];
  enabled: boolean;
  navigationScopeKey: string;
  onFocusDetection: (detection: TDetection) => void;
  /**
   * Optional comparator for ordering instances within a tag.
   * Defaults to pageNumber ascending (TakeoffPilot single-document behavior).
   * FieldPilot plan-set adapters should sort by documentOrder → page → bbox.
   */
  compareDetections?: (left: TDetection, right: TDetection) => number;
}

function defaultCompareDetections<TDetection extends TagInstanceNavigationDetection>(
  left: TDetection,
  right: TDetection
) {
  return left.pageNumber - right.pageNumber;
}

export function comparePlanSetTagDetections<
  TDetection extends TagInstanceNavigationDetection,
>(left: TDetection, right: TDetection) {
  const documentOrderDelta = (left.documentOrder ?? 0) - (right.documentOrder ?? 0);
  if (documentOrderDelta !== 0) return documentOrderDelta;
  const pageDelta = left.pageNumber - right.pageNumber;
  if (pageDelta !== 0) return pageDelta;
  const leftY = left.bbox?.y ?? 0;
  const rightY = right.bbox?.y ?? 0;
  if (leftY !== rightY) return leftY - rightY;
  return (left.bbox?.x ?? 0) - (right.bbox?.x ?? 0);
}

export function useTagInstanceNavigation<
  TTag extends TagInstanceNavigationTag,
  TDetection extends TagInstanceNavigationDetection,
>({
  tags,
  activeDetections,
  enabled,
  navigationScopeKey,
  onFocusDetection,
  compareDetections = defaultCompareDetections,
}: UseTagInstanceNavigationArgs<TTag, TDetection>) {
  const [focusedDetectionIdByTagId, setFocusedDetectionIdByTagId] =
    useState<Record<string, string>>({});
  const [highlightedDetectionId, setHighlightedDetectionId] = useState<string | null>(null);
  const focusedDetectionIdByTagIdRef = useRef<Record<string, string>>({});
  const highlightedDetectionIdRef = useRef<string | null>(null);
  const previousActiveDetectionsByTagIdRef = useRef<Map<string, TDetection[]>>(new Map());
  const onFocusDetectionRef = useRef(onFocusDetection);
  onFocusDetectionRef.current = onFocusDetection;

  const activeDetectionsByTagId = useMemo(() => {
    const detectionsByNormalizedText = new Map<string, TDetection[]>();
    for (const detection of activeDetections) {
      const existing = detectionsByNormalizedText.get(detection.normalizedText) ?? [];
      existing.push(detection);
      detectionsByNormalizedText.set(detection.normalizedText, existing);
    }

    for (const detections of detectionsByNormalizedText.values()) {
      detections.sort(compareDetections);
    }

    return new Map(
      tags.map((tag) => [
        String(tag.tagId),
        detectionsByNormalizedText.get(tag.normalizedValue) ?? [],
      ] as const)
    );
  }, [activeDetections, compareDetections, tags]);

  const navigableTagIds = useMemo(
    () =>
      enabled
        ? tags
            .filter((tag) => (activeDetectionsByTagId.get(String(tag.tagId))?.length ?? 0) > 0)
            .map((tag) => String(tag.tagId))
        : [],
    [activeDetectionsByTagId, enabled, tags]
  );

  const navigateTagInstance = useCallback(
    (tagId: unknown, direction: 1 | -1) => {
      if (!enabled) {
        return;
      }

      const tagKey = String(tagId);
      const detections = activeDetectionsByTagId.get(tagKey) ?? [];
      if (detections.length === 0) {
        return;
      }

      const currentDetectionId = focusedDetectionIdByTagIdRef.current[tagKey];
      const currentIndex = currentDetectionId
        ? detections.findIndex((detection) => String(detection._id) === currentDetectionId)
        : -1;
      const baseIndex =
        currentIndex === -1 ? (direction === 1 ? -1 : 0) : currentIndex;
      const nextDetection =
        detections[(baseIndex + direction + detections.length) % detections.length];
      if (!nextDetection) {
        return;
      }

      const nextDetectionId = String(nextDetection._id);
      onFocusDetection(nextDetection);
      highlightedDetectionIdRef.current = nextDetectionId;
      setHighlightedDetectionId(nextDetectionId);
      focusedDetectionIdByTagIdRef.current = {
        ...focusedDetectionIdByTagIdRef.current,
        [tagKey]: nextDetectionId,
      };
      setFocusedDetectionIdByTagId((current) => ({
        ...current,
        [tagKey]: nextDetectionId,
      }));
    },
    [activeDetectionsByTagId, enabled, onFocusDetection]
  );

  const focusNextInstanceForTag = useCallback(
    (tagId: unknown) => {
      navigateTagInstance(tagId, 1);
    },
    [navigateTagInstance]
  );

  const focusPreviousInstanceForTag = useCallback(
    (tagId: unknown) => {
      navigateTagInstance(tagId, -1);
    },
    [navigateTagInstance]
  );

  useEffect(() => {
    focusedDetectionIdByTagIdRef.current = focusedDetectionIdByTagId;
  }, [focusedDetectionIdByTagId]);

  useEffect(() => {
    highlightedDetectionIdRef.current = highlightedDetectionId;
  }, [highlightedDetectionId]);

  useEffect(() => {
    if (enabled) {
      return;
    }
    setHighlightedDetectionId(null);
    highlightedDetectionIdRef.current = null;
    focusedDetectionIdByTagIdRef.current = {};
    setFocusedDetectionIdByTagId({});
    previousActiveDetectionsByTagIdRef.current = new Map();
  }, [enabled]);

  useEffect(() => {
    setHighlightedDetectionId(null);
    highlightedDetectionIdRef.current = null;
    focusedDetectionIdByTagIdRef.current = {};
    setFocusedDetectionIdByTagId({});
    previousActiveDetectionsByTagIdRef.current = new Map();
  }, [navigationScopeKey]);

  useEffect(() => {
    const visibleTagIds = new Set(tags.map((tag) => String(tag.tagId)));
    // O(1) membership for focused/highlighted ids across the active set.
    const visibleDetectionIds = new Set(
      activeDetections.map((detection) => String(detection._id))
    );
    const previousByTagId = previousActiveDetectionsByTagIdRef.current;
    const currentFocus = focusedDetectionIdByTagIdRef.current;
    const nextFocus: Record<string, string> = {};
    const remappedDetections: TDetection[] = [];
    let hasFocusChanges = false;

    // Only iterate focused tags (usually 0–few), not the full detection list.
    for (const [tagId, detectionId] of Object.entries(currentFocus)) {
      if (!visibleTagIds.has(tagId)) {
        hasFocusChanges = true;
        continue;
      }

      if (visibleDetectionIds.has(detectionId)) {
        nextFocus[tagId] = detectionId;
        continue;
      }

      // Focused detection was removed (e.g. hidden). Advance to the next
      // remaining instance at the same list position, wrapping when needed.
      hasFocusChanges = true;
      const detections = activeDetectionsByTagId.get(tagId) ?? [];
      if (detections.length === 0) {
        continue;
      }

      const previousDetections = previousByTagId.get(tagId) ?? [];
      const removedIndex = previousDetections.findIndex(
        (detection) => String(detection._id) === detectionId
      );
      const remappedIndex =
        removedIndex === -1 ? 0 : removedIndex % detections.length;
      const remapped = detections[remappedIndex];
      if (!remapped) {
        continue;
      }

      nextFocus[tagId] = String(remapped._id);
      remappedDetections.push(remapped);
    }

    if (hasFocusChanges) {
      focusedDetectionIdByTagIdRef.current = nextFocus;
      setFocusedDetectionIdByTagId(nextFocus);
    }

    const currentHighlight = highlightedDetectionIdRef.current;
    const highlightStillValid =
      currentHighlight !== null && visibleDetectionIds.has(currentHighlight);

    if (!highlightStillValid) {
      let nextHighlight: string | null = null;

      if (currentHighlight) {
        for (const [tagId, detectionId] of Object.entries(currentFocus)) {
          if (detectionId !== currentHighlight) {
            continue;
          }
          const remappedId = nextFocus[tagId];
          if (remappedId && remappedId !== currentHighlight) {
            nextHighlight = remappedId;
            break;
          }
        }
      }

      if (nextHighlight === null && remappedDetections[0]) {
        nextHighlight = String(remappedDetections[0]._id);
      }

      highlightedDetectionIdRef.current = nextHighlight;
      setHighlightedDetectionId(nextHighlight);
    }

    for (const detection of remappedDetections) {
      onFocusDetectionRef.current(detection);
    }

    previousActiveDetectionsByTagIdRef.current = activeDetectionsByTagId;
  }, [activeDetections, activeDetectionsByTagId, tags]);

  return {
    focusNextInstanceForTag,
    focusPreviousInstanceForTag,
    highlightedDetectionId,
    navigableTagIds,
  };
}
